import { useState } from 'react'
import { adminApi } from '../../services/adminApi'
import type {
    EmailTemplate,
    GenerateTemplateDraftResult,
    TemplateCategory,
} from '../../types/communications'
import { PreviewPane } from './PreviewPane'

interface Props {
    onClose: () => void
    onSaved: (t: EmailTemplate) => void
}

export function AIGenerateTemplateDrawer({ onClose, onSaved }: Props) {
    const [prompt, setPrompt] = useState('')
    const [category, setCategory] = useState<TemplateCategory>('BROADCAST')
    const [draft, setDraft] = useState<GenerateTemplateDraftResult | null>(null)
    const [preview, setPreview] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)

    async function generate() {
        setBusy(true)
        setError(null)
        try {
            const r = await adminApi.aiGenerateTemplate(prompt, category)
            setDraft(r)
            setPreview(
                `<!DOCTYPE html><html><body style="margin:0;background:#F5F3EF;padding:20px;">${r.bodyHtml}</body></html>`,
            )
        } catch (e) {
            setError((e as Error).message)
        } finally {
            setBusy(false)
        }
    }

    async function save() {
        if (!draft) return
        setBusy(true)
        setError(null)
        try {
            const slug = draft.name
                .toLowerCase()
                .replace(/\s+/g, '_')
                .replace(/[^a-z0-9_]/g, '')
            const r = await adminApi.createEmailTemplate({
                key: slug || `ai_template_${Date.now()}`,
                subject: draft.subject,
                bodyHtml: draft.bodyHtml,
                variables: draft.variables,
                name: draft.name,
                description: draft.description,
                category: draft.category,
                source: 'AI_GENERATED',
                previewValues: draft.previewValues,
            })
            onSaved(r as EmailTemplate)
        } catch (e) {
            setError((e as Error).message)
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex">
            <button className="flex-1 bg-black/30" onClick={onClose} aria-label="close" />
            <div className="h-full w-[min(1200px,100vw)] overflow-y-auto bg-white p-6">
                <h2 className="mb-4 text-lg font-semibold">Generate template with AI</h2>
                {error && (
                    <div className="mb-3 rounded-md bg-red-50 p-3 text-sm text-red-800">
                        {error}
                    </div>
                )}
                <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-3">
                        <div>
                            <label className="mb-1 block text-sm font-medium">Category</label>
                            <select
                                value={category}
                                onChange={e => setCategory(e.target.value as TemplateCategory)}
                                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            >
                                <option value="BROADCAST">Broadcast</option>
                                <option value="CAMPAIGN">Campaign</option>
                                <option value="DIGEST_BLOCK">Digest block</option>
                            </select>
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium">
                                Describe the template you want
                            </label>
                            <textarea
                                value={prompt}
                                onChange={e => setPrompt(e.target.value)}
                                placeholder="e.g. A monthly roundup with a bold headline, 3–5 highlight bullets, and a CTA to the changelog."
                                className="block h-40 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            />
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={generate}
                                disabled={busy || !prompt.trim()}
                                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
                            >
                                {busy ? 'Generating…' : 'Generate'}
                            </button>
                            {draft && (
                                <button
                                    onClick={save}
                                    disabled={busy}
                                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm"
                                >
                                    Save to library
                                </button>
                            )}
                        </div>
                        {draft && (
                            <>
                                <div className="rounded-md bg-gray-50 p-3 text-xs">
                                    <div>
                                        <strong>Name:</strong> {draft.name}
                                    </div>
                                    <div>
                                        <strong>Description:</strong> {draft.description}
                                    </div>
                                    <div className="mt-1">
                                        <strong>Slots:</strong>{' '}
                                        {draft.variables.map(v => v.name).join(', ')}
                                    </div>
                                </div>
                                {draft.sanitizationWarnings.length > 0 && (
                                    <div className="rounded-md bg-yellow-50 p-3 text-xs">
                                        <strong>Sanitizer removed:</strong>{' '}
                                        {draft.sanitizationWarnings.join(', ')}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div>
                        <h3 className="mb-2 text-sm font-medium text-gray-600">Preview</h3>
                        <PreviewPane html={preview} />
                    </div>
                </div>
            </div>
        </div>
    )
}
